import { ReviewResponse } from './review';
import { StatEventBase, StatTypeGeneric, StatTypeExtended, StatType, StatEventJson } from './stat-event-base';
import { PlanType } from './subscription-plan';
import { DistributionChannel, DistributionChannelString } from './distribution-channel';

export { StatType, StatEventJson }

/***
 * A generic statistics event representing user interaction at a specific time, such as USER_CREATED, MOTE_OPENED, MOTE_UNLOCKED etc. 
 */
export class StatEventGeneric extends StatEventBase {
    statType: StatTypeGeneric = null;
}

/**
 * A 'directions' statistics event representing a visitor/user usage of on the directions feature including selected navigation profile
 */
export class StatEventDirections extends StatEventBase {
    statType: StatTypeExtended.DIRECTIONS_CLICKED = StatTypeExtended.DIRECTIONS_CLICKED;
    constructor(public addition: { profile: string }) {
        super({ statType: StatTypeExtended.DIRECTIONS_CLICKED, addition: addition });
    }
}

/**
 * A 'journey' statistics event representing a visitor/user usage of on the public transportation directions feature
 * including information on any route the user looked for and optional any API errors.
 */
export class StatEventJourney extends StatEventBase {
    statType: StatTypeExtended.JOURNEY_LOADED = StatTypeExtended.JOURNEY_LOADED;

    constructor(public addition: { from: string, to: string, err: string }) {
        super({ statType: StatTypeExtended.JOURNEY_LOADED, addition: addition });
    }
}

/**
 * A 'search' statistics event representing a visitor/user usage of search feature on the map including search query
 */
export class StatEventSearch extends StatEventBase {
    statType: StatTypeExtended.SEARCH_QUERY = StatTypeExtended.SEARCH_QUERY;

    constructor(public addition: { query: string }) {
        super({ statType: StatTypeExtended.SEARCH_QUERY, addition: addition });
    }
}

/**
 * A 'review' statistics event representing visitor/user responses to a tour review
 */
export class StatEventReview extends StatEventBase {
    statType: StatTypeExtended.TOUR_REVIEWED = StatTypeExtended.TOUR_REVIEWED;

    constructor(public addition: { [questionKey: string ]: ReviewResponse }, public tourKey: string) {
        super({ statType: StatTypeExtended.TOUR_REVIEWED, addition, tourKey });
    }
}

/**
 * A 'comment' statistics event containing mote comment added by visitor/user 
 */
export class StatEventComment extends StatEventBase {
    statType: StatTypeExtended.USER_COMMENT =  StatTypeExtended.USER_COMMENT;

    constructor(moteKey: string, public addition: { comment: string }) {
        super({ statType: StatTypeExtended.USER_COMMENT, addition: addition });
        this.moteKey = moteKey;
    }
}

/**
 * A 'voucher purchased' statistics event indicating that one or more voucher were purchased by user
 */
export class StatEventVoucherPurchased extends StatEventBase {
    statType: StatTypeExtended.VOUCHER_PURCHASED = StatTypeExtended.VOUCHER_PURCHASED;

    constructor(public addition: { amount: number, totalPrice: number, productKey: string, creatorPlan: PlanType, paymentSuccess: boolean, partnerCost: number, distributionChannel: DistributionChannelString }) {
        super({ statType: StatTypeExtended.VOUCHER_PURCHASED, addition: addition });
    }
}

/**
 * A 'voucher' statistics event indicating that a voucher was claimed by user (not yet redeemed)
 */
export class StatEventVoucherClaimed extends StatEventBase {
    statType: StatTypeExtended.VOUCHER_CLAIMED = StatTypeExtended.VOUCHER_CLAIMED;

    constructor(public addition: { voucherKey: string }) {
        super({ statType: StatTypeExtended.VOUCHER_CLAIMED, addition: addition });
    }
}

/**
 * A 'voucher' statistics event indicating that a voucher was redeemed
 */
export class StatEventVoucherRedeemed extends StatEventBase {
    statType: StatTypeExtended.VOUCHER_REDEEMED = StatTypeExtended.VOUCHER_REDEEMED;

    constructor(public addition: { voucherKey: string }) {
        super({ statType: StatTypeExtended.VOUCHER_REDEEMED, addition: addition });
    }
}

/**
 * A 'media consumed' statistics event indicating that a media item was consumed by the user
 */
export class StatEventMediaConsumed extends StatEventBase {
    statType: StatTypeExtended.MEDIA_CONSUMED = StatTypeExtended.MEDIA_CONSUMED;

    constructor(public addition: { mediaKey: string, consumedPct?: number }) {
        super({ statType: StatTypeExtended.MEDIA_CONSUMED, addition: addition });
    }
}

export enum SignupMotive {
    PERSONAL = "PERSONAL",
    PROFESSIONAL = "PROFESSIONAL",
    FREE_TOURS = "FREE_TOURS",
    CURIOUS = "CURIOUS",
    DIRECT_SIGNUP = "DIRECT_SIGNUP",
}

/**
 * A 'creator signup' statistics event representing a successful user signup to SendInBlue Creator Beta list
 * TODO: Add additional information about the user as with StatEventUserCreated
 */
export class StatEventCreatorSignup extends StatEventBase {
    statType: StatTypeExtended.CREATOR_SIGNUP = StatTypeExtended.CREATOR_SIGNUP;

    constructor(public addition: { signupMotive: SignupMotive, emailSignup: boolean }) {
        super({ statType: StatTypeExtended.CREATOR_SIGNUP, addition: addition });
    }
}

/**
 * A 'tour page visited' statistics event representing any user visiting the tour-info page
 */
export class StatEventTourPageVisited extends StatEventBase {
    statType: StatTypeExtended.TOUR_PAGE_VISITED = StatTypeExtended.TOUR_PAGE_VISITED;

    constructor(public addition: { firstVisit: boolean }) {
        super({ statType: StatTypeExtended.TOUR_PAGE_VISITED, addition: addition });
    }
}

/**
 * Explicit event when a user starts a tour (alternative to inference from first MOTE_UNLOCKED).
 */
export class StatEventTourStarted extends StatEventBase {
    statType: StatTypeExtended.TOUR_STARTED = StatTypeExtended.TOUR_STARTED;
}

/**
 * Explicit event when a user completes a tour (alternative to inference from last MOTE_UNLOCKED).
 */
export class StatEventTourCompleted extends StatEventBase {
    statType: StatTypeExtended.TOUR_COMPLETED = StatTypeExtended.TOUR_COMPLETED;
}

/**
 * Stats event indicating that a story aka a Mote was unlocked by an user.
 * FIRST_UNLOCKED - first story unlocked in the whole project, aka a new unique active user
 * MOTE_UNLOCKED - a user unlocked a second or consecutive story
 */
export class StatEventMoteUnlocked extends StatEventBase {
    statType: StatTypeExtended.MOTE_UNLOCKED | StatTypeExtended.FIRST_UNLOCKED =  StatTypeExtended.MOTE_UNLOCKED;

    constructor(statType: StatTypeExtended.MOTE_UNLOCKED | StatTypeExtended.FIRST_UNLOCKED, public addition: {
        // Number of stories unlocked by the user in the tour containing the unlocked story
        tourStoriesUnlocked: number,
        // Number of stories unlocked by the user in the project containing the unlocked story
        projectStoriesUnlocked: number,
        // The total amount of stories in the tour containing the unlocked story
        totalTourStories: number,
        // Order of the unlocked story of the tour
        storyOrder: number,
        // Whether unlock was forced (if user was not on location or gps was disabled)
        unlockWasForced: boolean
    }) {
        super({ statType, addition });
        // OBS: Why oh why is this needed (guess it is because default value in sub class is applied after call to super?)
        this.statType = statType;
    }
}

/**
 * Unlock of a mote on another tour while the session is attributed to this event's tourKey (same addition shape as MOTE_UNLOCKED).
 */
export class StatEventExtraMoteUnlocked extends StatEventBase {
    statType: StatTypeExtended.EXTRA_MOTE_UNLOCKED = StatTypeExtended.EXTRA_MOTE_UNLOCKED;

    constructor(public addition: {
        tourStoriesUnlocked: number,
        projectStoriesUnlocked: number,
        totalTourStories: number,
        storyOrder: number,
        unlockWasForced: boolean
    }) {
        super({ statType: StatTypeExtended.EXTRA_MOTE_UNLOCKED, addition });
    }
}

/**
 * A 'user created' statistics event representing a visitor/user that visited the project  
 */
export class StatEventUserCreated extends StatEventBase {
    statType: StatTypeExtended.USER_CREATED = StatTypeExtended.USER_CREATED;

    /** The country where the IP address of the user originates */
    public country?: string = null;
    /** The city where the IP address of the user originates */
    public city?: string = null;
    /** URI encoded referer from browser indicating which url the user was referred through */
    public referer?: string = null;
    /** String representing the language version reported by browser as defined in the BCP 47 standard */
    public language?: string = null;
    /** String representing the operating system and the browser type ie. 'iOS_Safari', 'Mac_Safari', 'android_chrome', 'linux_chrome', 'Windows_Chrome', 'Mac_FB-Messanger', 'unknown_unknown' etc. */
    public browser?: string = null;
    /** A string representing the type of device (reported by the device) at where the event occurred (ie 'iphone', 'iPhone', 'Android', 'unknown' etc.) */
    public device?: string = null;
    /** User agent reported by the browser on the device ie. '"Mozilla/5.0 (Linux; Android 7.0; SM-G920F)...' */
    public userAgent?: string = null;

    constructor(json: StatEventJson<number>) {
        super(json);
        this.fromJson(json);
    }

    public fromJson(json: any): StatEventUserCreated {
        super.fromJson(json);
        // Add additional json params (add it flat for created events instead of using addition as all types are)
        this.country = json.country;
        this.city = json.city;
        this.referer = json.referer;
        this.language = json.language;
        this.browser = json.browser;
        this.device = json.device;
        this.userAgent = json.userAgent;
        return this;
    }
}

/**
 * A 'user' visited landing page statistics event representing a visitor/user that visited the landing page (one event per session)
 */
export class StatEventLandingVisited extends StatEventBase {
    statType: StatTypeExtended.LANDING_VISITED = StatTypeExtended.LANDING_VISITED;
}

// Disable for now as we time get both a search query and a journey result 
// export class StatEventSearchResult extends StatEventBase {
//     statType: StatTypeAdd.SEARCH_RESULT_SELECTED

//     constructor(public addition: { result: string } ) {
//         super({statType:StatTypeAdd.SEARCH_RESULT_SELECTED, addition:addition});
//     }
// }

/**
 * Union of visitor (user) statistic events generated by a unique visitor
 */
export class VisitorStats {
    public visitKey: string;
    /** Timestamp representing when the visitor initiated the current session */
    public visitStartTime: Date = null;
    /** List of statistic events associated with the visitor */
    public stats: StatEvent[];
    /** City reported by visitor in user survey */
    public city?: string;
    /** Gender reported by visitor in user survey */
    public gender?: string;
    /** Age reported by visitor in user survey */
    public age?: number;
    /** USER_CREATED event with visitor information such as device, os, country (of IP address) and  browser language */
    public createStat?: StatEventUserCreated;
}

/**
 * A generic statistics event representing a visitor/user interaction at a specific time 
 */
export const StatEvent = StatEventGeneric;
type VoucherStats = StatEventVoucherPurchased | StatEventVoucherClaimed | StatEventVoucherRedeemed;
export type StatEvent = StatEventGeneric | StatEventUserCreated | StatEventLandingVisited | StatEventReview | StatEventComment | StatEventDirections | StatEventJourney | StatEventSearch | StatEventMediaConsumed | StatEventMoteUnlocked | StatEventExtraMoteUnlocked | StatEventTourPageVisited | StatEventTourStarted | StatEventTourCompleted | StatEventCreatorSignup | VoucherStats;
